import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBeta } from '../contexts/BetaContext';
import { useBetaData } from '../contexts/BetaDataContext';
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
} from '../api/notifications';
import { supabase } from '../supabaseClient';

/**
 * useNotifications - Hook for managing in-app notifications
 *
 * Features:
 * - unreadCount 由 BetaDataContext 的 HEAD 請求提供（初始載入零成本）
 * - 通知列表延遲到用戶打開面板時才載入（lazy load）
 * - Supabase Realtime subscription for new notifications
 * - Mark as read / mark all as read
 */
const useNotifications = () => {
  const { profile } = useAuth();
  const { isBetaMode } = useBeta();
  const { usersMapByEmail, unreadCount: contextUnreadCount } = useBetaData();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const notificationsLoadedRef = useRef(false);

  // 從 BetaDataContext 同步 unreadCount（來自 HEAD 請求，初始載入即可用）
  useEffect(() => {
    setUnreadCount(contextUnreadCount);
  }, [contextUnreadCount]);

  // Realtime subscription（不載入列表，只訂閱即時通知）
  useEffect(() => {
    if (!isBetaMode || !profile?.id) return;

    const channel = supabase
      .channel('user-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${profile.id}`,
        },
        () => {
          setUnreadCount((prev) => prev + 1);
          // Realtime payload 沒有 join 資料（缺 actor），需 refetch 完整資料
          if (notificationsLoadedRef.current) {
            loadNotifications();
          }
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [isBetaMode, profile?.id]);

  // Polling fallback：每 60 秒重拉 unreadCount，避免 Realtime 斷線時失效
  useEffect(() => {
    if (!isBetaMode || !profile?.id) return;

    const interval = setInterval(async () => {
      const { count } = await getUnreadCount(profile.id);
      setUnreadCount(count);
    }, 60_000);

    return () => clearInterval(interval);
  }, [isBetaMode, profile?.id]);

  // 載入通知列表（用戶打開面板時呼叫）
  // 每次開面板都重新查詢，避免快取過期導致看不到新通知
  const ensureNotificationsLoaded = useCallback(async () => {
    if (!profile?.id) return;
    const isFirstLoad = !notificationsLoadedRef.current;
    notificationsLoadedRef.current = true;
    if (isFirstLoad) setLoading(true);
    const { data } = await getNotifications(profile.id, { limit: 50 });
    setNotifications(data || []);
    setLoading(false);
  }, [profile?.id]);

  const loadNotifications = useCallback(async () => {
    if (!profile?.id) return;
    notificationsLoadedRef.current = true;
    setLoading(true);
    const { data } = await getNotifications(profile.id, { limit: 50 });
    setNotifications(data || []);
    setLoading(false);
  }, [profile?.id]);

  const handleMarkAsRead = useCallback(
    async (notificationId) => {
      // Optimistic：立即從列表移除
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      await markAsRead(notificationId);
    },
    []
  );

  const handleMarkAllAsRead = useCallback(async () => {
    if (!profile?.id) return;
    // Optimistic：立即清空列表
    setNotifications([]);
    setUnreadCount(0);
    await markAllAsRead(profile.id);
  }, [profile?.id]);

  // 用 usersMapByEmail 補上 actor 的 name/avatar_url（profiles 已無這些欄位）
  const enrichedNotifications = useMemo(() => {
    return notifications.map((n) => {
      if (!n.actor?.email) return n;
      const mainUser = usersMapByEmail[n.actor.email.toLowerCase()] || {};
      return {
        ...n,
        actor: {
          ...n.actor,
          name: mainUser.name || n.actor.email?.split('@')[0] || n.actor.email,
          avatar_url: mainUser.avatar_url || null,
        },
      };
    });
  }, [notifications, usersMapByEmail]);

  return {
    notifications: enrichedNotifications,
    unreadCount,
    loading,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    refresh: loadNotifications,
    ensureLoaded: ensureNotificationsLoaded,
  };
};

export default useNotifications;
